import Collapse from 'bootstrap/js/dist/collapse';
import { yieldToMain } from '../../components/helpers';
import {
  SIDEBAR_OPEN_CLASS,
  SIDEBAR_MINI_CLASS,
  getSidebarState,
  setSidebarState,
  permanentSidebarState,
  applyPermanentState,
  resolveSidebarLayout,
  openSidebarPeek,
  dismissSidebarPeek,
  isSidebarPeeking,
} from './sidebar_state';

// Single source of truth is top_bar.sass — read computed display rather than
// duplicating the responsive breakpoints in JS.
function topBarProvidesMenuAccess() {
  const topBar = document.querySelector('.top-bar');
  return !!topBar && window.getComputedStyle(topBar).display !== 'none';
}

// Splits the two open paths the design calls for: a hover-capable pointer (desktop) expands
// the rail on hover; a touch device (tablet) gets the tap-to-peek + backdrop flow instead.
// Keeping them mutually exclusive avoids the synthetic mouseenter a tap fires from pre-opening
// the active panel just before the click toggles it shut.
function deviceCanHover() {
  return !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);
}

// Accordion panels track the rail's open state in mini mode: collapsed while the rail is a
// mini rail, and the active universe's panel re-expands when the rail opens via hover, chevron
// or boot. A tablet peek-click opens the *tapped* panel via Bootstrap's own toggle, so it is
// not routed through here. Reuses the same Collapse instances as `sidebar_navigation_controller`
// so the `.show`/aria/`.collapsed` state stays consistent.
function eachAccordionPanel($menu, callback) {
  $menu.find('.accordion-collapse').each(function() {
    callback(Collapse.getOrCreateInstance(this, { toggle: false }), this);
  });
}

function closeAllAccordions($menu) {
  eachAccordionPanel($menu, (collapse) => collapse.hide());
}

function openActiveAccordion($menu) {
  const activeButton = $menu.find('.accordion-button.desktop-navigation__item--active').get(0);
  const targetSelector = activeButton && activeButton.getAttribute('data-bs-target');
  const activePanel = targetSelector ? $menu.find(targetSelector).get(0) : null;
  eachAccordionPanel($menu, (collapse, panel) => (panel === activePanel ? collapse.show() : collapse.hide()));
}

const initializeSidebar = async function() {
  await yieldToMain();
  configureSidebars();
  configureMiniSidebars();
};

function configureSidebars(force = false) {
  const $lateralMenu = $('#lateral-menu');
  const $adminLateralMenu = $('#admin-lateral-menu');
  if ($lateralMenu.length === 0 && $adminLateralMenu.length === 0) {
    return;
  }

  const lateralReady = $lateralMenu.length === 0 || $lateralMenu.hasClass('fxp-sidebar-ready');
  const adminReady = $adminLateralMenu.length === 0 || $adminLateralMenu.hasClass('fxp-sidebar-ready');

  if (!force && lateralReady && adminReady) {
    return;
  }

  $lateralMenu.removeClass('fxp-sidebar-mini fxp-sidebar-open fxp-sidebar-locked fxp-sidebar-force-open');
  $adminLateralMenu.removeClass('fxp-sidebar-mini fxp-sidebar-open fxp-sidebar-locked fxp-sidebar-force-open');

  if ($lateralMenu.hasClass('fxp-sidebar-ready')) {
    $lateralMenu.sidebar('destroy');
  }

  if ($adminLateralMenu.hasClass('fxp-sidebar-ready')) {
    $adminLateralMenu.sidebar('destroy');
  }

  // A sidebar opts into the collapsible behaviour (cookie-persisted mini/open, hover-to-expand,
  // toggle button, forced-mini observer) by carrying the data-sidebar-collapsible tag. Untagged
  // menus (editor & admin sidebars) keep the default page-class-driven, locked-open layout.
  const isCollapsible = $lateralMenu.is('[data-sidebar-collapsible]');
  const $page = $('#page');
  const layout = resolveSidebarLayout({
    collapsible: isCollapsible,
    width: $(window).width(),
    storedState: getSidebarState(),
    pageIsOpen: $page.length === 0 || $page.hasClass('page--lateral-menu-open'),
  });

  $lateralMenu.addClass(layout.retracted ? SIDEBAR_MINI_CLASS : SIDEBAR_OPEN_CLASS);
  $lateralMenu.sidebar({
    showObfuscator: layout.showObfuscator,
    classWrapper: 'fxp-sidebar-wrapper lateral-menu-wrapper',
    locked: layout.locked,
  });

  if ($('#toggle-sidebar').length > 0) {
    $('#lateral-menu').sidebar('attachToggle', $('#toggle-sidebar'));
  }

  // Drop any observer from a previous page before (maybe) re-creating it for the app nav,
  // so it never lingers while an editor menu is on screen.
  if (window._lateralMenuObserver) {
    window._lateralMenuObserver.disconnect();
    window._lateralMenuObserver = null;
  }

  if (isCollapsible) {
    $('.desktop-navigation__open-close-btn').off('click.sidebar-toggle').on('click.sidebar-toggle', function(e) {
      e.stopPropagation();
      // Explicit toggle settles the permanent state, so clear any peek backdrop/flag first.
      dismissSidebarPeek($lateralMenu[0]);
      if ($lateralMenu.hasClass(SIDEBAR_OPEN_CLASS)) {
        $lateralMenu.sidebar('close');
        $lateralMenu.addClass(SIDEBAR_MINI_CLASS);
        setSidebarState('mini');
        closeAllAccordions($lateralMenu);
      } else {
        $lateralMenu.sidebar('open');
        $lateralMenu.removeClass(SIDEBAR_MINI_CLASS);
        setSidebarState('open');
        openActiveAccordion($lateralMenu);
      }
    });

    // Mini mode only: hovering the links transiently expands the sidebar and opens the
    // active universe's accordion; leaving it reverts to the permanent state and re-collapses
    // the accordion. Hovering the rest of the sidebar leaves it mini (the open/close button
    // surfaces via CSS), so those areas are not bound here. `.brand-switcher-mount` is the Slim
    // container of the Vue brand switcher (its `.brand-switcher__trigger` button) — bound so it
    // expands the rail on hover like the other nav items; the container is always in the DOM, so
    // this is safe regardless of when the Vue component mounts.
    $lateralMenu.off('mouseenter.sidebar-hover mouseleave.sidebar-hover');
    $lateralMenu.find('.desktop-navigation__item, .desktop-navigation__role-switcher, .brand-switcher-mount').off('mouseenter.sidebar-hover')
      .on('mouseenter.sidebar-hover', function() {
        if (!deviceCanHover()) return;
        if (permanentSidebarState() !== 'mini') return;
        if ($lateralMenu.hasClass(SIDEBAR_MINI_CLASS)) {
          $lateralMenu.addClass(SIDEBAR_OPEN_CLASS).removeClass(SIDEBAR_MINI_CLASS);
          openActiveAccordion($lateralMenu);
        }
      });

    $lateralMenu.on('mouseleave.sidebar-hover', function() {
      // A click-opened "peek" stays put until the backdrop is dismissed — only a plain
      // hover-expand reverts here.
      if (isSidebarPeeking($lateralMenu[0])) return;
      // Keep the rail expanded while the brand-switcher dropdown is open (`brand-switcher--open`
      // on its mount): leaving the rail must not collapse it out from under the open menu.
      if ($lateralMenu[0].querySelector('.brand-switcher--open')) return;
      applyPermanentState($lateralMenu[0]);
      // Mini = accordion closed. In a persisted-open rail the user owns the accordion, so leave it.
      if (permanentSidebarState() === 'mini') closeAllAccordions($lateralMenu);
    });

    // Touch only (desktop uses hover): tapping a universe/account accordion button or the desktop
    // brand-switcher trigger "peeks" the sidebar open (no preference change) with a dismiss backdrop,
    // so touch users — who have no hover — can browse the rail. The Bootstrap collapse toggle on the
    // same button expands the tapped panel as usual.
    $lateralMenu.find('.accordion-button, .desktop-navigation .brand-switcher__trigger').off('click.sidebar-peek').on('click.sidebar-peek', function() {
      if (deviceCanHover()) return;
      if (permanentSidebarState() !== 'mini') return;
      openSidebarPeek($lateralMenu[0]);
    });

    $lateralMenu.parent().find('.fxp-sidebar-obfuscator').off('click.sidebar-peek').on('click.sidebar-peek', function() {
      dismissSidebarPeek($lateralMenu[0]);
      applyPermanentState($lateralMenu[0]);
      closeAllAccordions($lateralMenu);
    });

    window._lateralMenuObserver = new MutationObserver(function() {
      const isOpen = $lateralMenu.hasClass(SIDEBAR_OPEN_CLASS);
      const isMini = $lateralMenu.hasClass(SIDEBAR_MINI_CLASS);
      $('#page')
        .toggleClass('page--lateral-menu-open', isOpen)
        .toggleClass('page--lateral-menu-closed', !isOpen);

      // When the rail retracts (mini / no longer open), close an open brand-switcher dropdown so it
      // doesn't linger orphaned over the collapsed rail. The switcher listens for `sidebar:retracted`.
      // (Its own class lives on `.brand-switcher-mount`, outside this observer's target, so closing it
      // does not re-trigger this callback.)
      if ((isMini || !isOpen) && $lateralMenu[0].querySelector('.brand-switcher--open')) {
        document.dispatchEvent(new CustomEvent('sidebar:retracted'));
      }

      if (!isOpen && !isMini && !topBarProvidesMenuAccess()) {
        $lateralMenu.addClass(SIDEBAR_MINI_CLASS);
      }
    });
    window._lateralMenuObserver.observe($lateralMenu[0], { attributes: true, attributeFilter: ['class'] });

    // Settle the accordion to the booted rail state: collapsed when mini (overriding the
    // server-rendered active `.show`), active panel expanded when persisted-open or locked.
    if ($lateralMenu.hasClass(SIDEBAR_MINI_CLASS)) {
      closeAllAccordions($lateralMenu);
    } else {
      openActiveAccordion($lateralMenu);
    }
  }

  const enoughSpaceForAdmin = $(window).width() > 1500;

  $adminLateralMenu.addClass(!enoughSpaceForAdmin ? 'fxp-sidebar-mini' : 'fxp-sidebar-open');
  $adminLateralMenu.sidebar({
    showObfuscator: !enoughSpaceForAdmin,
    classWrapper: 'fxp-sidebar-wrapper admin-lateral-menu-wrapper',
    locked: enoughSpaceForAdmin,
  });
}

function configureMiniSidebars() {
  const $miniSidebars = $('.fxp-sidebar-mini').not('#lateral-menu[data-sidebar-collapsible]').not('[data-mini-hover-bound]');

  if ($miniSidebars.length > 0) {
    $miniSidebars.each(function(index) {
      const $miniSidebar = $miniSidebars.eq(index);
      $miniSidebar.attr('data-mini-hover-bound', 'true');

      $miniSidebar.on('mouseenter', null, null, function() {
        $miniSidebar.sidebar('open');
      });

      $miniSidebar.on('mouseleave', null, null, function() {
        $miniSidebar.sidebar('close');
      });
    });
  }
}

document.addEventListener('turbo:load', initializeSidebar);

const onResize = function() {
  clearTimeout(window.resizeEvt);
  window.resizeEvt = setTimeout(function() {
    configureSidebars(true);
    configureMiniSidebars();
  }, 250);
};

$(window).off('resize', onResize);
$(window).on('resize', onResize);
